import React, {useEffect, useMemo, useState} from 'react';
import {useTitle} from '../../components/General/Layout';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  TableContainer,
  Checkbox,
  Stack,
  InputLeftElement,
  Icon,
  Button,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  ModalFooter,
  useDisclosure,
  CircularProgress,
} from '@chakra-ui/react';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {get, queryStringBuilder, postDelete} from '../../service/ApiService';
import {BatchO} from '../../service/Urls';
import {formatDate} from '../../helpers/Functions';
import ErrorHelper from '../../service/ErrorHelper';
import ClientOSelect from '../../components/ClientO/ClientOSelect';
import {getServiceType} from '../../helpers/ServiceTypeDictionary';
import {FaSearch, FaTrash} from 'react-icons/fa';

const columns = [
  {
    key: 'batchNumber',
    label: 'Batch No.',
  },
  {
    key: 'importDate',
    label: 'Date Imported',
  },
  {
    key: 'startDate',
    label: 'Data From Date',
  },
  {
    key: 'endDate',
    label: 'Data To Date',
  },
  {
    key: 'recordCount',
    label: 'No. of Records',
  },
  {
    key: 'serviceType',
    label: 'Import Type',
  },
  {
    label: 'Deleted',
  },
  {
    key: 'dateDeleted',
    label: 'Date Deleted',
  },
];

export default function OppPlanBatchMaintenance() {
  const [_, setTitle] = useTitle('Opp Plan Batch Maintenance');

  const [toast, setToast] = useState(null);

  const [clientId, setClientId] = useState(null);
  const [filter, setFilter] = useState('');
  const [order, setOrder] = useState('importDate');
  const [orderDir, setOrderDir] = useState('desc');

  const {onOpen: onDeleteOpen, onClose: onDeleteClose, isOpen: isDeleteOpen} = useDisclosure();
  const [deleteId, setDeleteId] = useState(null);

  const queryClient = useQueryClient();

  const {data, isFetching} = useQuery(['batchO', clientId], () => get(queryStringBuilder(BatchO.LIST, {opportunityPlanId: clientId})), {
    keepPreviousData: true,
    enabled: !!clientId,
  });

  const {mutate: deleteBatch, isLoading: isDeleting} = useMutation({
    mutationFn: batchId => postDelete(BatchO.DELETEBATCH + batchId),
    onSuccess: result => {
      queryClient.invalidateQueries(['batchO']);

      if (result.error) {
        setToast({response: {error: true}, errorMessage: result.error});
      } else {
        setToast({successMessage: 'Successfully deleted batch.'});
      }
    },
    onError: () => {
      setToast({response: {error: true}, errorMessage: 'Error deleting batch.'});
    },
    onSettled: () => {
      onDeleteClose();
    },
  });

  const rows = useMemo(() => {
    if (!clientId) return [];

    const result = (data?.data ?? []).filter(
      row =>
        row.batchNumber?.toString().includes(filter) ||
        formatDate(row.importDate)?.includes(filter) ||
        formatDate(row.startDate)?.includes(filter) ||
        formatDate(row.endDate)?.includes(filter) ||
        row.recordCount?.toString().includes(filter) ||
        getServiceType(row.serviceType)?.key?.includes(filter) ||
        row.dateDeleted?.includes(filter)
    );

    result.sort((a, b) => {
      const multiplier = orderDir === 'asc' ? 1 : -1;

      return a[order]?.toString().localeCompare(b[order]?.toString()) * multiplier;
    });

    return result;
  }, [data, clientId, filter, order, orderDir]);

  const handleSort = column => {
    if (!column) return;

    if (order === column) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrder(column);
      setOrderDir('asc');
    }
  };

  useEffect(() => {
    setTitle('A/R Entry 2');
  }, []);

  return (
    <Stack spacing={5} p={5}>
      <Flex alignItems="flex-end" gap={2}>
        <Box width={500}>
          <ClientOSelect onChange={value => setClientId(value?.id ?? null)} />
        </Box>

        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color="gray.400" />
          </InputLeftElement>

          <Input placeholder="Search..." onChange={e => setFilter(e.target.value)} />
        </InputGroup>
      </Flex>

      <TableContainer height="36em" sx={{overflow: 'auto'}} position="relative">
        {isFetching && (
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            alignItems="center"
            justifyContent="center"
            bg="rgba(255, 255, 255, 0.1)"
            zIndex={99}
          >
            <CircularProgress isIndeterminate size="30px" color="teal" />
          </Flex>
        )}

        <Table size="sm" variant="striped" colorScheme="mint">
          <Thead>
            <Tr>
              <Th></Th>
              {columns.map(({key, label}) => (
                <Th key={label} onClick={() => handleSort(key)} cursor="pointer">
                  {label} <span style={{visibility: key === order ? 'visible' : 'hidden'}}>{orderDir === 'asc' ? '▲' : '▼'}</span>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map(row => {
              return (
                <Tr key={row.batchId}>
                  <Td>
                    <IconButton
                      icon={<FaTrash />}
                      size="sm"
                      colorScheme="red"
                      onClick={() => {
                        onDeleteOpen();
                        setDeleteId(row.batchId);
                      }}
                    />
                  </Td>
                  <Td borderRight="1px solid var(--chakra-colors-gray-100)">{row.batchNumber}</Td>
                  <Td borderRight="1px solid var(--chakra-colors-gray-100)">{formatDate(row.importDate)}</Td>
                  <Td borderRight="1px solid var(--chakra-colors-gray-100)">{formatDate(row.startDate)}</Td>
                  <Td borderRight="1px solid var(--chakra-colors-gray-100)">{formatDate(row.endDate)}</Td>
                  <Td borderRight="1px solid var(--chakra-colors-gray-100)">{row.recordCount}</Td>
                  <Td borderRight="1px solid var(--chakra-colors-gray-100)">{getServiceType(row.serviceType)?.key}</Td>
                  <Td borderRight="1px solid var(--chakra-colors-gray-100)">
                    <Checkbox isChecked={!!row.dateDeleted} isDisabled />
                  </Td>
                  <Td borderRight="1px solid var(--chakra-colors-gray-100)">{formatDate(row.dateDeleted)}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>

      <Modal blockScrollOnMount={false} size={'lg'} isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Batch</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure you want to delete this batch?</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Close
            </Button>
            <Button colorScheme="red" onClick={() => deleteBatch(deleteId)} disabled={isDeleting}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ErrorHelper toastResponse={toast} />
    </Stack>
  );
}
